import React, { Fragment } from 'react';
import cx from 'classnames';

import NavLink from 'components/navV2/NavLink';
import NavArrowLink from 'components/navV2/NavArrowLink/NavArrowLink';
import { MenuMenuSectionItem, NavItemClickTrackHandler } from 'components/navV2/NavData/types';

import css from './subMenuColumn.module.less';

type SubMenuColumnProps = {
  headline: string;
  headlinePath?: string;
  links: MenuMenuSectionItem[];
  borderLeft?: boolean;
  onNavItemClick: NavItemClickTrackHandler;
  columnPosition: number;
  twoColumns?: boolean;
  className?: string;
  secondHeadline?: string;
  secondHeadlinePath?: string;
  secondLinks?: MenuMenuSectionItem[];
};

const SubMenuColumn = ({
  headline,
  headlinePath,
  links,
  borderLeft,
  onNavItemClick,
  columnPosition,
  twoColumns,
  className,
  secondHeadline,
  secondHeadlinePath,
  secondLinks,
}: SubMenuColumnProps): JSX.Element => {
  const isHeadlineLink = Boolean(headlinePath);

  const getRowPosition = (index: number) => {
    const ONE_INDEXED = 1; // rowPosition is 1-indexed;
    let headlineOffset = 0;
    if (isHeadlineLink) headlineOffset += 1;
    return ONE_INDEXED + index + headlineOffset;
  };

  const renderHeadline = (headlineStr: string, pathStr?: string, marginTop?: boolean) => {
    if (pathStr)
      return (
        <a
          href={pathStr}
          className={cx(css.title, {
            [css.marginTop]: marginTop,
          })}
          onClick={() =>
            onNavItemClick({
              linkText: headline,
              rowPosition: 1,
              columnPosition,
            })
          }
        >
          {headlineStr}
        </a>
      );
    return (
      <div
        className={cx(css.title, {
          [css.marginTop]: marginTop,
        })}
      >
        {headlineStr}
      </div>
    );
  };

  const getAltText = (title: string | JSX.Element): string =>
    typeof title === 'string' ? title : '';

  const renderLinks = (linkList: MenuMenuSectionItem[]) => (
    <ul className={cx(css.list, { [css.twoColumnContainer]: twoColumns })}>
      {linkList.map((link, i) => (
        <li key={i} className={cx({ [css.twoColumnLink]: twoColumns })}>
          {link.arrowLink ? (
            <NavArrowLink
              href={link.path}
              newTag={link.newTag}
              onClick={() =>
                onNavItemClick({
                  linkText: link.title,
                  rowPosition: getRowPosition(i),
                  columnPosition,
                })
              }
            >
              {link.title}
            </NavArrowLink>
          ) : (
            <NavLink
              href={link.path}
              className={cx(css.link, link.className, {
                [css.thumbnailLink]: link.thumbnail,
              })}
              newTag={link.newTag}
              icon={link.icon}
              onClick={() =>
                onNavItemClick({
                  linkText: link.title,
                  rowPosition: getRowPosition(i),
                  columnPosition,
                })
              }
            >
              {link.thumbnail && (
                <img src={link.thumbnail} alt={getAltText(link.title)} className={css.thumbnail} />
              )}
              {link.title}
            </NavLink>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <div
      className={cx(className, css.column, { [css.borderLeft]: borderLeft, 'two-col': twoColumns })}
    >
      {renderHeadline(headline, headlinePath)}
      {renderLinks(links)}
      {secondHeadline && secondLinks ? (
        <Fragment>
          {renderHeadline(secondHeadline, secondHeadlinePath, true)}
          {renderLinks(secondLinks)}
        </Fragment>
      ) : null}
    </div>
  );
};

export default SubMenuColumn;
